import * as React from "react";
import { styled } from "@mui/material";
import {
  SortType,
  SummaryDiseaseCluster,
  SummaryDiseaseClusterFragment,
} from "fe-common/graphql";
import { useMemo } from "react";
import { scrollableStyle } from "fe-common/ui/commonStyles";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import { CommonColors } from "fe-web-components/colors/commonColors";
import CommonPatientCardFeatureItem from "./commonPatientCardFeatureItem";
import {
  onAddonAndThemeBreakPoint,
  PATIENT_CARD_HEADER_DESKTOP_HEIGHT,
} from "./patientCardCommonUi";
//region [[ Styles ]]
export const PatientCardDdView = styled("div")(({ theme }) => ({
  ...onAddonAndThemeBreakPoint(theme.breakpoints.down("md"), {
    maxHeight: "fit-content",
    overflow: "initial",
  }),
  "&.scrollable": {
    maxHeight: `calc(100vh - ${PATIENT_CARD_HEADER_DESKTOP_HEIGHT + 100}px)`,
    overflow: "auto",
    ...scrollableStyle,
  },
}));

export const SubHeader = styled("div")({
  fontWeight: 700,
  marginBottom: "16px",
  "&:not(:first-of-type)": {
    marginTop: "20px",
  },
  color: CommonColors.Default,
});

const DDList = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 8,
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientCardDdProps {
  className?: string;
  dd: SummaryDiseaseCluster[];
  sortType: SortType;
  onDiseasesClicked?: (cluster: SummaryDiseaseClusterFragment) => void;
  hideFirstSubHeader?: boolean;
  scrollable?: boolean;
  inExtension?: boolean;
  simpleView?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const CommonPatientCardDd = ({
  dd,
  sortType = SortType.PatientProb,
  scrollable = true,
  simpleView,
  ...props
}: PatientCardDdProps) => {
  const { translate } = useTranslateByGender();

  const [mostLikely, other] = useMemo(() => {
    const mostLikely: SummaryDiseaseClusterFragment[] = [],
      other: SummaryDiseaseClusterFragment[] = [];
    dd?.sort(
      (a, b) => b?.scores?.adjustedNormalized - a?.scores?.adjustedNormalized,
    );
    dd?.forEach((d) => {
      if (d?.inTopCRPerSortType?.includes(sortType)) mostLikely.push(d);
      else other.push(d);
    });
    return [mostLikely, other];
  }, [dd, sortType]);

  return (
    <PatientCardDdView
      className={`${props.className} patient-card-dd-view ${
        scrollable ? "scrollable" : ""
      }`}
    >
      {!props.hideFirstSubHeader && (
        <SubHeader>{translate("MOST_LIKELY_CAUSES")}</SubHeader>
      )}
      <DDList>
        {mostLikely?.map?.((cluster, index) => (
          <CommonPatientCardFeatureItem
            key={index}
            cluster={cluster}
            strength={cluster?.scores?.adjustedNormalized}
            onClick={() => props.onDiseasesClicked?.(cluster)}
            inExtension={props.inExtension}
            index={index}
            simpleView={simpleView}
          />
        ))}
      </DDList>
      {other?.length > 0 && (
        <>
          <SubHeader>{translate("OTHER_CAUSES")}</SubHeader>
          <DDList>
            {other?.map?.((cluster, index) => (
              <CommonPatientCardFeatureItem
                key={index}
                cluster={cluster}
                strength={cluster?.scores?.adjustedNormalized}
                onClick={() => props.onDiseasesClicked?.(cluster)}
                inExtension={props.inExtension}
                index={index + (mostLikely?.length || 0)}
                simpleView={simpleView}
              />
            ))}
          </DDList>
        </>
      )}
    </PatientCardDdView>
  );
};
